import { IObject } from "@quest/adapter-interface";
import {
  BaseView,
  Bitmap,
  Container,
  delayedCall,
  dispatcher,
  ingenuity,
  ITween,
  killDelayedCall,
} from "@quest/base-core";
import { BitmapImage, Emitter } from "@quest/pixi-bridge";
import { BaseGameModel, SlotConstants, SlotEventConstants } from "@quest/slot-core";
import { eventDispatchers } from "../Constants/EventDispatchers";
import { GameParserModel } from "../Platform/GameParserModel";
import { gameConstants } from "../Constants/gameConstants";
import { SoundConstantGame } from "../Constants/SoundConstantGame";
import { SlotConstantsBehavior, SlotEventConstantsBehavior } from "@quest/murka-games-behavior-panther";

export class TrailController {
  private view: any;
  protected trailView: BaseView;
  protected model: BaseGameModel;
  protected trailParticle: Bitmap;
  protected trailStaticContainer: Container;
  protected trailAnimationContainer: Container;
  protected smokeSprite: BitmapImage;
  protected trailData: IObject[];

  // Trail Final Position Config
  protected trailSymConfig: IObject = {
    "11": ["red_trail", -50, -100, "Note"],
    "12": ["pink_trail", 81, -100, "Magnify"],
    "13": ["blue_trail", 200, -100, "Key"],
  };

  constructor(BasegameView: any, cascadeView: BaseView, model: any) {
    this.view = BasegameView;
    this.trailView = cascadeView;
    this.model = model;
    this.subscribeEvents();
    this.initializeComponent();
  }
  // Particle Data
  private particleData: any = {
    emitterType: 1,
    id: "coinEmitter_1",
    autoStart: false,
    animConfig: [
      {
        framerate: 20,
        loop: true,
        image: "red_trail",
        generateFrames: false,
        frame: 0,
      },
    ],
  };
  private particleConfig: any = {
    alpha: {
      start: 1,
      end: 0,
    },
    scale: {
      start: 3,
      end: 0.6,
      minimumScaleMultiplier: 1,
    },
    color: {

    },
    speed: {
      start: 0,
      end: 0,
      minimumSpeedMultiplier: 1,
    },
    acceleration: {
      x: 0,
      y: 0,
    },
    maxSpeed: 0,
    lifetime: {
      min: 0.2,
      max: 1,
    },
    blendMode: "screen",
    frequency: 0.001,
    emitterLifetime: -1,
    maxParticles: 300,
    pos: {
      x: 0,
      y: 0,
    },
    addAtBack: false,
    spawnType: "point"
  };

  /**
   * Subscribe  Events
   */
  // protected unsubscribeEvents(): void {
  //   dispatcher.off(eventDispatchers.START_SCATTER_TRAIL_FG, this.findSymbolPosition, this);
  // }

  /**
   * Subscribe  Events
   */
  protected subscribeEvents(): void {
    // this.unsubscribeEvents();
    if (ingenuity.currentGame.state.getCurrentState().key === SlotConstants.freeGameState) {
      dispatcher.on(eventDispatchers.START_SCATTER_TRAIL_FG, this.findSymbolPosition, this);
    } else {
      dispatcher.on(eventDispatchers.START_SCATTER_TRAIL, this.findSymbolPosition, this);
    }
  }

  /**
   * Initialize components
   */
  private initializeComponent(): void {
    this.trailAnimationContainer = this.trailView.getContainerByID("trailAnimationContainer");
    this.trailStaticContainer = this.trailView.getContainerByID("trailStaticContainer");
  }
  /**
   * Fetch Symbol Position and parse data
   */
  private findSymbolPosition(): void {
    this.view.addChild(this.trailView);
    this.trailData = (ingenuity.parserModel as GameParserModel).getSpecialSymbolData();
    this.trailData.length > 0 && dispatcher.fireEvent(SlotEventConstantsBehavior.HIDE_FREE_GAME_WIN_METER);
    this.trailData.forEach((data) => {
      this.playTrailAnimation(data.offset, data.symbol);
    });
    delayedCall(
      "delayForNextSequence",
      gameConstants.TIMER_1000,
      () => {
        killDelayedCall("delayForNextSequence");
        (ingenuity.parserModel as GameParserModel).setIsSpecialSymbol(false);
        this.model.getIsWin() && dispatcher.fireEvent(SlotEventConstants.SHOW_NEXT_WIN_PRESENTATION);
        !this.model.getIsWin() && dispatcher.fireEvent(eventDispatchers.ON_SHOW_NEXT_PRESENTATION);
      },
      this
    );
  }


  /**
   * funtion Play Trail animation
   * @param : offset and symbol data
   */
  private playTrailAnimation(symOffset: any, symbol: any): void {
    //Use to create trail image
    this.createTrailImage(symOffset[0], symOffset[1], this.trailSymConfig[symbol][0]);
    //Use to play trail animation on each scatter landed
    this.playScatterTrailAnimation(this.trailSymConfig[symbol][1], this.trailSymConfig[symbol][2], symbol);
    //Use to create trail emitter
    this.createTrailEmitter(this.trailSymConfig[symbol][0], symbol);
  }
  /**
   * Create Trail Static Image
   * @param : offesets for symbol and symbol id
   */
  private createTrailImage(colNum: number, rowNum: number, symbol: string): void {
    this.smokeSprite = new BitmapImage(ingenuity.currentGame, 0, 0, symbol);
    this.smokeSprite.x = colNum * 140;
    this.smokeSprite.y = rowNum * 90;
    this.particleData.id = this.trailView.json.smokeSprite.id + "_" + colNum + "_" + rowNum;
    this.smokeSprite.name = this.trailView.json.smokeSprite.name + "_" + colNum + "_" + rowNum;
    this.smokeSprite.alpha = this.trailView.json.smokeSprite.alpha;
    this.smokeSprite.scale.set(this.trailView.json.smokeSprite.scale);
    this.smokeSprite.anchor.set(this.trailView.json.smokeSprite.anchorX, this.trailView.json.smokeSprite.anchorY);
    this.trailStaticContainer.addChild(this.smokeSprite);
  }
  /**
   * Genrate Trail Animation for symbol movement
   * @param : Trail static image as per symbol
   */
  private createTrailEmitter(trailSprite: any, symbol: number): void {
    let data: any = this.getThemeColor(symbol);
    this.particleConfig.color = data.themeColor;
    this.particleData.animConfig[0].image = trailSprite;
    const particle: Emitter = ingenuity.currentGame.add.emitter(
      this.trailAnimationContainer,
      this.particleData,
      this.particleConfig
    ) as Emitter;
    particle.autoUpdate = true;
    particle.emit = true;
    particle.updateOwnerPos(this.smokeSprite.x, this.smokeSprite.y);
  }

  /**
   * function to set color of trail as per skull symbol
   * @param col 
   * @param row 
   * @returns 
   */
  private getThemeColor(symbol: number): Object {
    let data = {
      themeColor: {
        start: "#bd563d",
        end: "#eb1221",
      }
    };
    switch (symbol) {
      case gameConstants.SPECIAL_SYMBOL_NOTE_ID:
        data = {
          themeColor: {
            start: "#bd563d",
            end: "#eb1221",
          },
        };
        break;
      case gameConstants.SPECIAL_SYMBOL_MAGNIFY_ID:
        data = {
          themeColor: {
            start: "#2B0057",
            end: "#B163FF",
          },
        };
        break;
      case gameConstants.SPECIAL_SYMBOL_KEY_ID:
        data = {
          themeColor: {
            start: "#328FED",
            end: "#1F86ED",
          },
        };

        break;
    }
    return data;
  }

  /**
   * Play Tween to move symbol from real to progress bar meters
   * @param : final position on respective meters
   */
  private playScatterTrailAnimation(finalX: any, finalY: any, symbol: string): void {
    let trailTween: ITween;
    let curveVal = 0.7;
    let position = this.bezierCurve(this.smokeSprite.x, this.smokeSprite.y, finalX, finalY, curveVal);

    trailTween = ingenuity.currentGame.add.tween(this.smokeSprite).to({ x: position[0], y: position[1] }, 300, undefined, true);
    let trailTweenscale: ITween;
    trailTweenscale = ingenuity.currentGame.add.tween(this.smokeSprite.scale).to({ x: 0.65, y: 0.65 }, 300, undefined, true);
    trailTween.onComplete.addOnce(() => {
      trailTween.onComplete.dispose();
       (ingenuity.soundManager as any).playSound(SoundConstantGame.SPECIAL_PB_FILL, SlotConstantsBehavior.SOUND_LIST_PRELOAD);
      if (ingenuity.currentGame.state.getCurrentState().key === SlotConstants.baseGameState) {
        dispatcher.fireEvent(eventDispatchers.UPDATE_METER_VALUES, { symbol: symbol });
      } else {
        dispatcher.fireEvent(eventDispatchers.UPDATE_METER_VALUES_FG, { symbol: symbol });
      }
      delayedCall(`destroyTrails${trailTween.target.name}`, 100, () => {
        killDelayedCall(`destroyTrails${trailTween.target.name}`)
        let em: Emitter;
        em = ingenuity.currentGame.particles.getEmitterById(trailTween.target.name);
        em.autoUpdate = false;
        em.emit = false;
        em.destroy();
        trailTween.stop();
        trailTween.onUpdateCallback = null;
        trailTweenscale.stop();
        trailTweenscale.onUpdateCallback = null;
        ingenuity.currentGame.tweens.remove(trailTween);
        ingenuity.currentGame.tweens.remove(trailTweenscale);
        trailTween.target.parent.removeChild(trailTween.target);
      })
    }, this);
    delayedCall(`destroyTrailsupadate${trailTween.target.name}`, 100, () => {
      killDelayedCall(`destroyTrailsupadate${trailTween.target.name}`)
      trailTween.onUpdateCallback((evt: IObject) => {
        let em: Emitter;
        em = ingenuity.currentGame.particles.getEmitterById(evt.target.name);
        em.updateOwnerPos(evt.target.x, evt.target.y);
      });
    });
  }

  //create a curve path array position
  private bezierCurve(startX: number, startY: number, endX: number, endY: number, curveFactor: number): number[][] {
    // startX: X-coordinate of the starting point of the line
    // startY: Y-coordinate of the starting point of the line
    // endX: X-coordinate of the ending point of the line
    // endY: Y-coordinate of the ending point of the line
    // curveFactor: Factor determining the amount of curvature in the curve

    // Calculate control point coordinates
    const controlX = (startX + endX) / 2 - Math.abs(startX - endX) * curveFactor; // X-coordinate of the control point (midpoint)
    const controlY = (startY + endY) / 2; // Y-coordinate of the control point

    // Arrays to store X and Y coordinates of points along the curve
    let curveX: number[] = []; // Array to store X coordinates
    let curveY: number[] = []; // Array to store Y coordinates

    // Number of points to calculate along the curve
    const numberOfPoints = 50; // You can adjust this value based on the desired smoothness of the curve

    // Loop to calculate coordinates of points along the curve
    for (let i = 0; i <= numberOfPoints; i++) {
      const t = i / numberOfPoints; // Parameterization along the Bézier curve (ranges from 0 to 1)
      const x = Math.pow(1 - t, 2) * startX + 2 * (1 - t) * t * controlX + Math.pow(t, 2) * endX; // X-coordinate of the point on the curve
      const y = Math.pow(1 - t, 2) * startY + 2 * (1 - t) * t * controlY + Math.pow(t, 2) * endY; // Y-coordinate of the point on the curve
      curveX.push(x); // Add X-coordinate to the array
      curveY.push(y); // Add Y-coordinate to the array
    }

    // Return arrays of X and Y coordinates of points along the curve
    return [curveX, curveY];
  }

}
